import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact one-glance Omega automation health pill.
 * Fetches /api/omega/brain/health every 30s and color-codes itself.
 * Tap to expand a tray with: scanner status, kill switches, recovery,
 * tier, decisions count, win rate — all from /omega/automation/snapshot.
 */
export default function AutomationHealthPill({ compact = false }) {
  const [health, setHealth] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [open, setOpen] = useState(false);
  const [loadingSnap, setLoadingSnap] = useState(false);

  useEffect(() => {
    let dead = false;
    const tick = () => {
      axios.get(`${API}/omega/brain/health`)
        .then(r => { if (!dead) setHealth(r.data); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { dead = true; clearInterval(id); };
  }, []);

  const openTray = async () => {
    setOpen(true);
    if (snapshot) return;
    setLoadingSnap(true);
    try {
      const r = await axios.get(`${API}/omega/automation/snapshot`);
      setSnapshot(r.data);
    } catch (e) {
      setSnapshot({ error: e?.message || "failed" });
    } finally { setLoadingSnap(false); }
  };

  if (!health) return null;

  // Color: green = ok, yellow = warnings only, red = errors / kill switch fired
  const hasErr = (health.errors || []).length > 0 || health.kill_switches_fired;
  const hasWarn = (health.warnings || []).length > 0 || health.recovery_active || health.scanner_running === false;
  const tone = hasErr ? "red" : hasWarn ? "amber" : "green";
  const palette = {
    green: { bg: "rgba(74,222,128,0.10)", border: "rgba(74,222,128,0.32)", dot: "#4ade80", text: "rgba(220,255,225,0.92)" },
    amber: { bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.35)", dot: "#fbbf24", text: "rgba(255,240,200,0.92)" },
    red:   { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.42)", dot: "#f87171", text: "rgba(255,210,210,0.95)" },
  }[tone];
  const label = hasErr ? "Omega halted" : hasWarn ? "Omega watching" : "Omega healthy";

  return (
    <>
      <button
        type="button"
        data-testid="automation-health-pill"
        onClick={openTray}
        title="Omega Automation status — tap for details"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: compact ? "4px 9px" : "5px 11px",
          borderRadius: 999,
          background: palette.bg, border: `1px solid ${palette.border}`,
          color: palette.text, fontSize: compact ? 10 : 11, fontWeight: 700,
          cursor: "pointer", letterSpacing: 0.3, whiteSpace: "nowrap",
        }}
      >
        <span style={{
          width: 7, height: 7, borderRadius: 999, background: palette.dot,
          boxShadow: `0 0 10px ${palette.dot}`,
          animation: tone === "red" ? "omegaPulse 1.2s infinite" : "none",
        }} />
        {label}
      </button>
      {open && (
        <div
          data-testid="automation-health-tray"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 9998,
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "flex-start", justifyContent: "center",
            padding: "10vh 16px 16px",
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 480,
              background: "var(--layer1, #0f0f1e)",
              border: `1px solid ${palette.border}`,
              borderRadius: 18, padding: 18,
              color: "var(--text, #e6e6f5)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.55)",
              maxHeight: "82vh", overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.2 }}>Omega Automation</div>
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "var(--muted, #888)", fontSize: 20, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
            </div>
            {loadingSnap && <div style={{ fontSize: 12, color: "var(--muted, #888)" }}>Loading…</div>}
            {snapshot?.error && <div style={{ color: "#f87171", fontSize: 12 }}>{snapshot.error}</div>}
            {snapshot && !snapshot.error && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Row label="Overall" value={snapshot.overall?.healthy ? "✅ Healthy" : "⚠ Issues"} />
                {(snapshot.overall?.warnings || []).map((w, i) => (
                  <div key={`w-${i}`} style={{ fontSize: 11, color: "#fbbf24" }}>⚠ {w}</div>
                ))}
                {(snapshot.overall?.errors || []).map((e, i) => (
                  <div key={`e-${i}`} style={{ fontSize: 11, color: "#f87171" }}>⛔ {e}</div>
                ))}
                <div style={{ height: 1, background: "var(--border, rgba(255,255,255,0.08))", margin: "4px 0" }} />
                <SubsystemRow name="Scanner" sub={snapshot.subsystems?.scanner}
                  formatter={s => `${s.running ? "running" : "stopped"} · ${s.market_open ? "market open" : "market closed"} · last ${s.last_scan_age_sec != null ? Math.round(s.last_scan_age_sec) + "s ago" : "never"} · ${s.picks_in_last_scan || 0} picks`} />
                <SubsystemRow name="Kill switches (Real-Money Account)" sub={snapshot.subsystems?.kill_switches}
                  formatter={s => s.any_fired ? `⛔ FIRED (${(s.switches || []).filter(k => k.fired).map(k => k.name).join(", ")})` : `✅ all clear · equity $${s.equity?.toLocaleString()} · day P&L $${s.daily_pnl?.toLocaleString()}`} />
                <SubsystemRow name="Recovery mode (Real-Money Account)" sub={snapshot.subsystems?.recovery_mode}
                  formatter={s => s.active ? `🟡 ACTIVE · ${s.trade_count}/${s.target_trades} trades · size ${s.size_reduction} · gate ${s.gate_widening} · zBucks unaffected` : "off"} />
                <SubsystemRow name="Drift" sub={snapshot.subsystems?.drift}
                  formatter={s => s.detected ? `⚠ ${s.reason}` : (s.reason || "none")} />
                <SubsystemRow name="Tier / Autonomy" sub={snapshot.subsystems?.tier_autonomy}
                  formatter={s => `${s.tier_label} · ${s.autonomy_level} (min Zi ${s.min_zi_required}) · ${s.autopilot_mode}`} />
                <SubsystemRow name="Decisions" sub={snapshot.subsystems?.decision_ledger}
                  formatter={s => `${s.count_total} total · last ${(s.recent || []).slice(0, 3).map(d => `${d.symbol || "?"}(${Math.round(d.zi_score || 0)})`).join(", ")}`} />
                <SubsystemRow name="Guardian" sub={snapshot.subsystems?.guardian}
                  formatter={s => `${s.enabled ? "on" : "off"} · ${s.recent_alerts} alerts/24h`} />
                <SubsystemRow name="Auto-Trim" sub={snapshot.subsystems?.auto_trim}
                  formatter={s => `${s.enabled ? "on" : "off"} · ${s.recent_trims} trims/24h`} />
                <SubsystemRow name="Paper stats" sub={snapshot.subsystems?.paper_stats}
                  formatter={s => `${s.open_positions} open · ${s.closed_trades} closed · ${s.win_rate_pct}% win rate`} />
                {/* iter 132 — Recovery Replay + Why-Skipped quick-access */}
                <div style={{ height: 1, background: "var(--border, rgba(255,255,255,0.08))", margin: "4px 0" }} />
                <MorningBriefingBlock />
                <RecoveryReplayBlock />
                <WhySkippedBlock />
              </div>
            )}
          </div>
        </div>
      )}
      <style>{`@keyframes omegaPulse { 0%,100%{opacity:1;}50%{opacity:0.35;} }`}</style>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function SubsystemRow({ name, sub, formatter }) {
  if (!sub) return null;
  if (sub.degraded) {
    return (
      <div>
        <div style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{name}</div>
        <div style={{ fontSize: 11, color: "#f87171" }}>⚠ degraded — {sub.error}</div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{name}</div>
      <div style={{ fontSize: 12, color: "var(--text, #e6e6f5)" }}>{formatter(sub)}</div>
    </div>
  );
}

function MorningBriefingBlock() {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [tone, setTone] = useState("BEST_FRIEND");
  const [busy, setBusy] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  const load = async (t = tone) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/omega/briefing/morning?tone=${t}`);
      setData(r.data?.data || null);
      setText(r.data?.text || "");
    } catch {
      setText("Briefing unavailable right now.");
    }
    setBusy(false);
  };

  useEffect(() => { load("BEST_FRIEND"); /* eslint-disable-next-line */ }, []);

  const send = async (channel) => {
    setSendStatus({ pending: true });
    try {
      const r = await axios.post(`${API}/omega/briefing/morning/send`, { channels: [channel], tone });
      setSendStatus(r.data?.[channel] || { sent: false, error: "no response" });
    } catch (e) {
      setSendStatus({ sent: false, error: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <div data-testid="morning-briefing-block">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1 }}>Morning briefing</span>
        <div style={{ display: "flex", gap: 4 }}>
          {["BEST_FRIEND", "PROFESSIONAL"].map(t => (
            <button
              key={t}
              data-testid={`mb-tone-${t}`}
              onClick={() => { setTone(t); load(t); }}
              style={{
                padding: "2px 8px", borderRadius: 999, border: "1px solid",
                background: tone === t ? "rgba(96,165,250,0.18)" : "transparent",
                borderColor: tone === t ? "rgba(96,165,250,0.42)" : "rgba(255,255,255,0.08)",
                color: tone === t ? "#60a5fa" : "var(--muted, #aaa)",
                fontSize: 9, fontWeight: 700, letterSpacing: 0.4, cursor: "pointer",
              }}
            >{t === "BEST_FRIEND" ? "Casual" : "Pro"}</button>
          ))}
        </div>
      </div>
      <div data-testid="mb-text" style={{
        padding: "8px 10px", borderRadius: 8,
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.06)",
        fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-line",
        minHeight: 40, color: "var(--text, #e6e6f5)",
      }}>{busy ? "Brewing…" : text}</div>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button
          data-testid="mb-send-sms"
          onClick={() => send("sms")}
          disabled={busy}
          style={{
            flex: 1, padding: "6px 10px", borderRadius: 7, border: "none",
            background: "rgba(74,222,128,0.14)", color: "#4ade80",
            fontSize: 11, fontWeight: 800, cursor: busy ? "wait" : "pointer",
          }}
        >📱 Text it to me</button>
        <button
          data-testid="mb-send-email"
          onClick={() => send("email")}
          disabled={busy}
          style={{
            flex: 1, padding: "6px 10px", borderRadius: 7, border: "none",
            background: "rgba(251,191,36,0.14)", color: "#fbbf24",
            fontSize: 11, fontWeight: 800, cursor: busy ? "wait" : "pointer",
          }}
        >✉️ Email it</button>
      </div>
      {sendStatus && (
        <div data-testid="mb-send-status" style={{ fontSize: 10, marginTop: 4, color: sendStatus.sent ? "#4ade80" : "#f87171" }}>
          {sendStatus.pending ? "Sending…" :
            sendStatus.sent ? `✓ Sent to ${sendStatus.to}` :
              `⚠ ${sendStatus.error || "send failed"}`}
        </div>
      )}
    </div>
  );
}

function RecoveryReplayBlock() {
  const [recap, setRecap] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/omega/loss-recovery/replay?limit=3`)
      .then(r => setRecap(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRecap([]));
  }, []);
  if (!recap) return null;
  if (recap.length === 0) {
    return (
      <div>
        <div style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Recovery replay</div>
        <div style={{ fontSize: 11, color: "var(--muted, #aaa)", fontStyle: "italic" }}>No deactivated plans yet.</div>
      </div>
    );
  }
  return (
    <div>
      <button data-testid="recovery-replay-toggle" onClick={() => setOpen(o => !o)} style={{
        background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "inherit",
      }}>
        <span style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1 }}>Recovery replay</span>
        <span style={{ fontSize: 11, color: "var(--muted, #aaa)" }}>({recap.length})</span>
        <span style={{ fontSize: 11, color: "var(--muted, #aaa)", transform: open ? "rotate(90deg)" : "none", transition: "transform 150ms" }}>▸</span>
      </button>
      {open && (
        <div data-testid="recovery-replay-list" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
          {recap.map((r, i) => (
            <div key={i} style={{
              padding: "8px 10px", borderRadius: 8,
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              fontSize: 11, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700 }}>{r.headline}</div>
              <div style={{ color: "var(--muted, #aaa)", marginTop: 2 }}>
                {r.wins}W/{r.losses}L · {r.win_rate_pct}% win rate · Zi≥{r.zi_gate_min} · ${r.per_trade_risk_cap}/trade
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WhySkippedBlock() {
  const [sym, setSym] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const ask = async () => {
    const s = sym.trim().toUpperCase();
    if (!s) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/omega/automation/why-skipped/${s}?hours=24`);
      setData(r.data);
    } catch (e) {
      setData({ verdict: "Lookup failed: " + (e?.message || "unknown") });
    }
    setBusy(false);
  };
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted, #aaa)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Why didn't Omega fire?</div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          data-testid="why-skipped-input"
          value={sym}
          onChange={e => setSym(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") ask(); }}
          placeholder="Ticker (e.g. AAPL)"
          style={{
            flex: 1, padding: "7px 10px", borderRadius: 8,
            background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
            color: "var(--text, #e6e6f5)", fontSize: 12, letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        />
        <button
          data-testid="why-skipped-ask"
          onClick={ask}
          disabled={busy || !sym.trim()}
          style={{
            padding: "7px 14px", borderRadius: 8, border: "none",
            background: "rgba(96,165,250,0.18)", color: "#60a5fa",
            fontSize: 12, fontWeight: 800, cursor: busy ? "wait" : "pointer",
          }}
        >{busy ? "…" : "Ask"}</button>
      </div>
      {data && (
        <div data-testid="why-skipped-result" style={{
          marginTop: 6, padding: "8px 10px", borderRadius: 8,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          fontSize: 12, lineHeight: 1.5,
        }}>
          {data.verdict}
          {data.final_zi != null && <div style={{ color: "var(--muted, #aaa)", marginTop: 4, fontSize: 11 }}>Last computed Zi: {data.final_zi}</div>}
        </div>
      )}
    </div>
  );
}
